import {
  Avatar,
  Badge,
  Box,
  Chip,
  Drawer,
  GlobalStyles,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  useMuiSwitchableTheme,
  usePageTintWhileOpen,
} from '@farmshare/ui-components';
import { HelpOutline, Person } from '@mui/icons-material';
import { showIntercomMessages } from 'lib/intercom';
import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { useCognitoAuth } from '../../auth/cognito-auth-context';
import { userState } from 'state';

import { ProducerNavModule } from './producer-nav-config';
import { ProfileMenu } from './profile-menu';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  modules: ProducerNavModule[];
  activeModule: string | null;
  /**
   * The shell's module handler — the SAME one `IconRail` gets.
   *
   * The drawer used to interpret the module contract itself and only handled
   * `href`, so every `action: 'callback'` module was silently dead on a phone.
   * "Request a Job" is the first item in the menu and takes that branch for
   * any producer who has a processor, which is most of them. One handler, one
   * contract, no second interpretation to drift.
   */
  onClickModule: (mod: ProducerNavModule) => void;
}

export function MobileDrawer({
  open,
  onClose,
  modules,
  activeModule,
  onClickModule,
}: MobileDrawerProps) {
  const navigate = useNavigate();
  const user = useRecoilValue(userState);
  const { impersonateUserId } = useCognitoAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { marginTop } = useMuiSwitchableTheme();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // The page behind the menu wears the menu's colour while it is open — the
  // same arrangement as the processor nav, from the same hook. Past the
  // breakpoint the drawer is `display: none` and nothing is left to
  // transition out.
  const { tintPage, onExited: releasePageTint } = usePageTintWhileOpen(
    open && isMobile,
    isMobile,
  );

  const handleModuleClick = (mod: ProducerNavModule) => {
    // Close first: a callback module opens a modal, and leaving the drawer up
    // behind it would cover the thing the tap just opened.
    onClose();
    onClickModule(mod);
  };

  const handleAvatarClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const initial = user?.first_name?.charAt(0).toUpperCase();

  return (
    <>
      {/* A fixed panel stops at the top of Safari's floating toolbar; only the
          page itself paints behind it. Without this a pale band of page
          background shows through the toolbar under the green panel. */}
      {tintPage && (
        <GlobalStyles
          styles={{
            'html, body': {
              backgroundColor: `${muiTheme.palette.primary.dark} !important`,
            },
          }}
        />
      )}
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        hideBackdrop
        slotProps={{
          transition: {
            // Release the page tint only once the menu has fully closed — see
            // `tintPage` above.
            onExited: releasePageTint,
          },
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          // Keeps the modal root off the top bar so the hamburger stays tappable.
          top: `${marginTop}px`,
          '& .MuiDrawer-paper': {
            width: 1,
            // NOT `mt`. MUI's temporary paper is `position: fixed; height: 100%`,
            // which resolves against the VIEWPORT — a margin offsets the paper
            // without shrinking it, so it runs to `marginTop + 100vh` and the
            // overhang is unreachable rather than scrollable (the inner Stack
            // fills the paper exactly, so nothing overflows to scroll). Offset
            // via `top` and pin the bottom instead of measuring a height —
            // the paper then ends at the top of Safari's toolbar whatever
            // state the toolbar is in (see the viewport notes in index.html).
            top: `${marginTop}px`,
            bottom: 0,
            height: 'auto',
            // Only the home-indicator inset: the Profile row sits just above
            // the toolbar. The side insets clear the notch in landscape.
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            // `primary.light` with `primary.contrastText` fails WCAG AA — the
            // contrast token is derived from `primary.main`, so white lands on
            // the wrong swatch (3.18:1 light / 2.52:1 dark). `primary.main`
            // itself only clears AA in the light theme (4.56:1; dark mode's
            // lightened main is 3.31:1), so the drawer takes `primary.dark`,
            // which carries the same white text at 7.6:1 / 6.0:1.
            bgcolor: 'primary.dark',
          },
        }}
      >
        <Stack sx={{ flex: 1, minHeight: 0, pt: 4, py: 2, px: 1.5 }}>
          {/* Nav items */}
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            {modules.map((mod) => {
              const isActive = mod.id === activeModule;
              return (
                <Stack
                  key={mod.id}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  onClick={() => handleModuleClick(mod)}
                  role="button"
                  tabIndex={0}
                  aria-current={isActive ? 'page' : undefined}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleModuleClick(mod);
                    }
                  }}
                  // `action.selected`/`action.hover` are dark alphas tuned for a
                  // paper surface — near-invisible on the drawer's green ground,
                  // which left `fontWeight: 600` doing all the work of marking
                  // the current page. Tint with the text colour instead, so the
                  // affordance tracks whatever `contrastText` resolves to.
                  sx={(theme) => ({
                    px: 1.5,
                    py: 2,
                    cursor: 'pointer',
                    borderRadius: 2,
                    bgcolor: isActive
                      ? alpha(theme.palette.primary.contrastText, 0.18)
                      : 'transparent',
                    '&:active': {
                      bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
                    },
                  })}
                >
                  <Box
                    sx={{
                      color: 'primary.contrastText',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 36,
                    }}
                  >
                    {/* Unread badge parity with the desktop IconRail (PRO-607
                        Task 6). The module already carries badgeCount via
                        options.chatUnreadCount; a falsy value hides the badge. */}
                    <Badge
                      badgeContent={mod.badgeCount ?? 0}
                      color="error"
                      invisible={!mod.badgeCount}
                      slotProps={{
                        badge: {
                          'aria-label': mod.badgeCount
                            ? `${mod.badgeCount} in ${mod.label}`
                            : undefined,
                        },
                      }}
                    >
                      {mod.icon}
                    </Badge>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: isActive ? 600 : 400,
                      color: 'primary.contrastText',
                    }}
                  >
                    {mod.label}
                  </Typography>
                  {mod.beta && (
                    <Chip
                      label="Beta"
                      color="secondary"
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>

          {/* Bottom: Help + Profile */}
          <Stack spacing={0.5}>
            <Stack
              direction="row"
              alignItems="center"
              // 1.5, like every other row: at gap 2 the Help label sat 4px right
              // of the nav items above it and the Profile row below it.
              gap={1.5}
              onClick={() => {
                showIntercomMessages();
                onClose();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  showIntercomMessages();
                  onClose();
                }
              }}
              sx={(theme) => ({
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                borderRadius: 2,
                '&:active': {
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
                },
              })}
            >
              {/* Matches its own label — `text.secondary` is a paper-surface
                  token and left this the one mismatched glyph in the list.
                  Boxed to 36 so the label stays aligned with the nav items while
                  the glyph runs larger: HelpOutline draws its ring at ~83% of the
                  font size, so 43px is what puts the drawn circle at 36 — the
                  same width as the Profile ring below it. */}
              <Box
                sx={{
                  width: 36,
                  display: 'flex',
                  justifyContent: 'center',
                  color: 'primary.contrastText',
                }}
              >
                <HelpOutline sx={{ fontSize: 43 }} />
              </Box>
              <Typography variant="body1" color="primary.contrastText">
                Help
              </Typography>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              gap={1.5}
              onClick={handleAvatarClick}
              role="button"
              tabIndex={0}
              aria-label="Account menu"
              aria-haspopup="true"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setAnchorEl(e.currentTarget);
                }
              }}
              sx={(theme) => ({
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                borderRadius: 2,
                '&:active': {
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.1),
                },
              })}
            >
              {/* A filled `primary.main` disc on a `primary.dark` drawer read as
                  a pale-green blob — a shade off the ground rather than part of
                  it, and visually heavier than every other row's bare glyph.
                  Outlined in the text colour instead, at the full 36px the rest
                  of the rail's glyphs occupy. The 3px ring is not arbitrary: an
                  MUI outline icon strokes 2 of its 24 viewBox units, which is
                  what Help's ring measures at this size. */}
              <Box
                sx={{ width: 36, display: 'flex', justifyContent: 'center' }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'transparent',
                    color: 'primary.contrastText',
                    border: '3px solid currentColor',
                    fontSize: '1rem',
                    fontWeight: 600,
                    ...(impersonateUserId && { borderColor: 'error.main' }),
                  }}
                >
                  {initial || <Person sx={{ fontSize: 20 }} />}
                </Avatar>
              </Box>
              <Typography variant="body1" color="primary.contrastText">
                Profile
              </Typography>
            </Stack>

            <ProfileMenu
              anchorEl={anchorEl}
              onClose={handleClosePopover}
              // Dismissing the popover is not enough — the drawer is a full-screen
              // overlay, so without this "Account" changed the URL behind a menu
              // that stayed open.
              onNavigate={onClose}
              // The anchor is the full-width Profile row, so the menu lands the
              // width of the drawer it belongs to.
              matchAnchorWidth
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              onLogout={() => {
                onClose();
                setTimeout(() => {
                  navigate('/login');
                }, 100);
              }}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            />
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
}
